import PropTypes from 'prop-types';
// @flow

import React, { Component } from 'react';
import _, { rest } from 'lodash';
import I18n from 'PMT/i18n/I18n';

// -- constants
import ApiConstants from 'PMT/constants/ApiConstants';
import ViewConstants from 'constants/ViewConstants';
import { Constants } from 'constants';

// -- utils
import { PhoneUtils, ViewUtils } from 'PMT/utils';
import {
  LocaleChooser,
} from 'PMT/components/form';
import SecurityContext from 'utils/permissions/SecurityContext';

// -- entities
import Restaurant from 'PMT/entities/Restaurant';
import RestaurantMultipart from 'PMT/entities/RestaurantMultipart';

// -- views
import {
  TextInput,
  TextareaInput,
  ImageDropzone,
  SimplePhoneInput,
  RestaurantTagChooser,
  CountryChooser,
  TimeZoneChooser,
  NumberInput
} from 'PMT/components/form';
import RestaurantFormComponent from './RestaurantFormComponent';

export default class PublicFormView extends RestaurantFormComponent {

  static propTypes = {
    restaurant: PropTypes.instanceOf(Restaurant),

    /**
    * @param RestaurantMultipart
    */
    onUpdate: PropTypes.func.isRequired
  };

  state = this.getDefaultState(this.props.restaurant);

  canEdit(): boolean {
    return SecurityContext.restaurantSettings.editPublicInfo();
  }

  // -- tools

  //
  // RestaurantFormComponent override.
  //

  getDefaultState(restaurant: Restaurant = null) {
    return {
      image: null,
      imageFullSize: null,
      logo: null,
      logoFullSize: null, // didn't used for the moment.
      phonePub: PhoneUtils.format(restaurant.phonePub),
      webSitePub: restaurant.webSitePub,
      emailPub: restaurant.emailPub,
      desc: restaurant.desc,
      tag: this.getTagValue(restaurant.tag),
      facebookPub: restaurant.facebookPub,
      twitterPub: restaurant.twitterPub,
      address: restaurant.address.copy(),
      timeZone: restaurant.timeZone,
      geoPt: restaurant.geoPt.copy(),
      locale: restaurant.locale,
    };
  }

  haveModifications() {
    const restaurant: Restaurant = this.restaurant;

    return !(_.isNull(this.state.image)
    && PhoneUtils.format(restaurant.phonePub) === PhoneUtils.format(this.state.phonePub)
    && restaurant.webSitePub === this.state.webSitePub
    && restaurant.emailPub === this.state.emailPub
    && restaurant.desc === this.state.desc
    && restaurant.twitterPub === this.state.twitterPub
    && restaurant.facebookPub === this.state.facebookPub
    && this.getTagValue(restaurant.tag) === this.state.tag
    && restaurant.timeZone === this.state.timeZone
    && restaurant.address.equals(this.state.address)
    && restaurant.geoPt.equals(this.state.geoPt)
    && restaurant.locale === this.state.locale
    && _.isNull(this.state.logo));
  }


  getTagValue(tag) {
    return I18n.tr(tag);
  }

  //
  // actions form
  //

  onTimeZoneChange = (timeZone) => {
    this.setState({
      timeZone
    });
  };

  onLocaleChange = (locale) => {
    this.setState({
      locale
    });
  };

  onLogoChange = (pictureData: string, logoFullSizeData: string) => {
    this.setState({
      logo: pictureData,
      logoFullSize: _.isNull(pictureData) ? null : logoFullSizeData
    });
  };

  onPictureChange = (pictureData: string, pictureFullSizeData: string) => {
    this.setState({
      image: pictureData,
      imageFullSize: _.isNull(pictureData) ? null : pictureFullSizeData
    });
  };

  /**
  * See react-chooser
  * @param tag
  */
  onTagChange = (tag) => {
    this.setState({
      tag
    });
  };

  onFacebookChange = (facebookPub: string) => {
    this.setState({
      facebookPub
    });
  };

  onTwitterChange = (twitterPub: string) => {
    this.setState({
      twitterPub
    });
  };


  onPhonePubChange = (phonePub: string) => {
    this.setState({
      phonePub
    });
  };

  onEmailPubChange = (emailPub: string) => {
    this.setState({
      emailPub,
    });
  }

  onWebsiteChange = (webSitePub: string) => {
    this.setState({
      webSitePub
    });
  };

  onDescriptionChange = (desc: string) => {
    this.setState({
      desc
    });
  };

  onAddressStreetChange = (street: string) => {
    const address = this.state.address;
    address.street = street;
    this.setState({
      address
    });
  };

  onAddressPostcodeChange = (postCode: string) => {
    const address = this.state.address;
    address.postCode = postCode;
    this.setState({
      address
    });
  };

  onAddressCityChange = (city: string) => {
    const address = this.state.address;
    address.city =  city;
    this.setState({
      address
    });
  };

  onAddressCountryChange = (country: string) => {
    const address = this.state.address;
    address.country =  country;
    this.setState({
      address
    });
  };

  onLatitudeChange = (latitude) => {
    const geoPt = this.state.geoPt;
    geoPt.latitude = latitude;
    this.setState({
      geoPt
    });
  };

  onLongitudeChange = (longitude) => {
    const geoPt = this.state.geoPt;
    geoPt.longitude = longitude;
    this.setState({
      geoPt
    });
  };


  onSubmit = (event) => {
    event.preventDefault();

    if (!this.canEdit()) {
      return;
    }

    const restaurantMultipart:RestaurantMultipart = new RestaurantMultipart();

    if (!_.isNull(this.state.logo)) {
      restaurantMultipart.logo = ViewUtils.dataUriToFile(this.state.logo);
    }

    if (!_.isNull(this.state.image)) {
      restaurantMultipart.coverPicture = ViewUtils.dataUriToFile(this.state.image);
    }

    if (!_.isNull(this.state.imageFullSize)) {
      restaurantMultipart.coverPictureFullSize = ViewUtils.dataUriToFile(this.state.imageFullSize);
    }

    restaurantMultipart.externalId = this.state.externalId;
    restaurantMultipart.webSitePub = this.state.webSitePub;
    restaurantMultipart.desc = this.state.desc;
    restaurantMultipart.tag = this.state.tag;
    restaurantMultipart.facebookPub = this.state.facebookPub;
    restaurantMultipart.twitterPub = this.state.twitterPub;
    restaurantMultipart.emailPub = this.state.emailPub;
    restaurantMultipart.phonePub = this.state.phonePub;
    restaurantMultipart.address = this.state.address.copy();
    restaurantMultipart.geoPt = this.state.geoPt;
    restaurantMultipart.timeZone = this.state.timeZone;
    restaurantMultipart.locale = this.state.locale;

    this.props.onUpdate(restaurantMultipart);
  };

  //
  // renderers
  //

  renderTimeZone() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.public.timezone')}
          </label>
        </div>

        <div className="col-xs-6 fake-input-group">
          <TimeZoneChooser
            value={this.state.timeZone}
            onChange={this.onTimeZoneChange}
            clearable={false}
            readOnly={!this.canEdit()}
          />
        </div>
      </div>
    );
  }

  renderLocale() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.public.locale')}
          </label>
        </div>

        <div className="col-xs-6 fake-input-group">
          <LocaleChooser
              locales={Constants.localesForTranslation
              .map(l => l.locale.replace('-', '_'))}
            readOnly={!this.canEdit()}
            onChange={this.onLocaleChange}
            value={this.state.locale}
            multi={false}
          />
        </div>
      </div>
    );
  }

  renderLogo() {
    const placeholder = _.isNull(this.restaurant.logo) ?
    ViewConstants.Image.DEFAULT_RESTAURANT_LOGO : this.restaurant.logo;

    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.public.logo')}
          </label>
        </div>

        <div className="col-xs-8 dropzone_restaurant_logo">
          <ImageDropzone
            placeholder={placeholder}
            image={this.state.logo}
            onChange={this.onLogoChange}
            aspectRatio={ApiConstants.Image.RestaurantLogo.RATIO}
            width={ApiConstants.Image.RestaurantLogo.WIDTH}
            height={ApiConstants.Image.RestaurantLogo.HEIGHT}
            maxSize={ApiConstants.Image.RestaurantLogo.MAX_SIZE}
            readOnly={!this.canEdit()}
          />
          <br />
          <em>{I18n.tr('restaurant_edit.public.logo_requirements')}</em>
        </div>
      </div>
    );
  };

  renderPicture() {
    const placeholder = _.isNull(this.restaurant.picture) ?
    ViewConstants.Image.DEFAULT_RESTAURANT : this.restaurant.picture;

    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.public.picture')}
          </label>
        </div>

        <div className="col-xs-8 dropzone_restaurant_image">
          <ImageDropzone
            placeholder={placeholder}
            image={this.state.image}
            onChange={this.onPictureChange}
            aspectRatio={ApiConstants.Image.RESTAURANT_PICTURE_RATIO}
            maxSize={ApiConstants.Image.RESTAURANT_PICTURE_MAX_SIZE}
            readOnly={!this.canEdit()}
            width={ApiConstants.Image.RESTAURANT_PICTURE_WIDTH}
            height={ApiConstants.Image.RESTAURANT_PICTURE_HEIGHT}
          />
        </div>

      </div>
    );
  }

  renderTag() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.public.tag')}
          </label>
        </div>

        <div className="col-xs-6">
          <RestaurantTagChooser
            tag={this.state.tag}
            onChange={this.onTagChange}
            readOnly={!this.canEdit()}
          />
        </div>
      </div>
    );
  }

  renderFacebook() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>Facebook</label>
        </div>

        <div className="col-xs-6">
          <div className="input-group">
            <TextInput
              type="url"
              placeholder="https://www.facebook.com/paymytable/"
              value={this.state.facebookPub}
              onChange={this.onFacebookChange}
              readOnly={!this.canEdit()}
            />
            <div className="input-group-addon">
              <label>
                <i className="fa fa-facebook" />
              </label>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderTwitter() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>Twitter</label>
        </div>

        <div className="col-xs-6">
          <div className="input-group">
            <TextInput
              type="url"
              placeholder="https://twitter.com/PayMyTable"
              value={this.state.twitterPub}
              onChange={this.onTwitterChange}
              readOnly={!this.canEdit()}
            />
            <div className="input-group-addon">
              <label>
                <i className="fa fa-twitter" />
              </label>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderEmail() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>{I18n.tr('restaurant_edit.public.email')}</label>
        </div>

        <div className="col-xs-6">
          <div className="input-group">
            <TextInput
              type="email"
              placeholder={Constants.CONTACT_EMAIL}
              value={this.state.emailPub}
              onChange={this.onEmailPubChange}
              readOnly={!this.canEdit()}
            />
            <div className="input-group-addon">
              <label>
                <i className="fa fa-envelope" />
              </label>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderPhoneNumber() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.public.phone_number')}
          </label>
        </div>

        <div className="col-xs-6">
          <div className="input-group">
            <SimplePhoneInput
              className="form-control"
              value={this.state.phonePub}
              onChange={this.onPhonePubChange}
              readOnly={!this.canEdit()}
            />
            <div className="input-group-addon">
              <label>
                <i className="fa fa-phone" />
              </label>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderWebsite() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.public.website')}
          </label>
        </div>

        <div className="col-xs-6">
          <div className="input-group">
            <TextInput
              type="text"
              placeholder="https://paymytable.com"
              value={this.state.webSitePub}
              onChange={this.onWebsiteChange}
              readOnly={!this.canEdit()}
            />
            <div className="input-group-addon">
              <label>
                <i className="fa fa-external-link" />
              </label>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderStreet() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.public.address.label')}
          </label>
        </div>

        <div className="col-xs-6">
          <div className="input-group">
            <TextInput
              value={this.state.address.street}
              onChange={this.onAddressStreetChange}
              isRequired
              readOnly={!this.canEdit()}
            />
            <div className="input-group-addon">
              <label>
                <i className="fa fa-map-marker" />
              </label>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderPostalCode() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.public.address.post_code')}
          </label>
        </div>

        <div className="col-xs-6">
          <div className="input-group">
            <TextInput
              value={this.state.address.postCode}
              onChange={this.onAddressPostcodeChange}
              isRequired
              readOnly={!this.canEdit()}
            />
            <div className="input-group-addon">
              <label>
                <i className="fa fa-map-marker" />
              </label>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderCity() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.public.address.city')}
          </label>
        </div>

        <div className="col-xs-6">
          <div className="input-group">
            <TextInput
              value={this.state.address.city}
              onChange={this.onAddressCityChange}
              isRequired
              readOnly={!this.canEdit()}
            />
            <div className="input-group-addon">
              <label>
                <i className="fa fa-map-marker" />
              </label>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderCountry() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.public.address.country')}
          </label>
        </div>

        <div className="col-xs-6 fake-input-group">
          <CountryChooser
            country={this.state.address.country}
            onChange={this.onAddressCountryChange}
            clearable={false}
            readOnly={!this.canEdit()}
          />
        </div>
      </div>
    );
  }

  renderCoordinates() {
    const geoPt = this.state.geoPt;
    const googleMapUrl = `https://www.google.com/maps?q=${geoPt.latitude},${geoPt.longitude}`;

    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.public.gps_coordinates')}
          </label>
        </div>

        <div className="col-xs-3">
          <div className="input-group">
            <NumberInput
              value={geoPt.latitude}
              onChange={this.onLatitudeChange}
              readOnly={!this.canEdit()}
              format={NumberInput.GEOPT_FORMAT}
            />
            <div className="input-group-addon">
              {I18n.tr('restaurant_edit.public.latitude')}
            </div>
          </div>
        </div>
        <div className="col-xs-3">
          <div className="input-group">
            <NumberInput
              value={geoPt.longitude}
              onChange={this.onLongitudeChange}
              format={NumberInput.GEOPT_FORMAT}
              readOnly={!this.canEdit()}
            />
            <div className="input-group-addon">
              {I18n.tr('restaurant_edit.public.longitude')}
            </div>
          </div>
        </div>

        <div className="col-xs-6 col-xs-offset-4 top-buffer-10">
          <a
            href={googleMapUrl}
            target="_blank"
          >
            {I18n.tr('restaurant_edit.public.preview')}
          </a>
        </div>
      </div>
    );
  }

  renderDescription() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>
            {I18n.tr('restaurant_edit.public.description')}
          </label>
        </div>

        <div className="col-xs-6">
          <TextareaInput
            placeholder=""
            rows={6}
            value={this.state.desc}
            onChange={this.onDescriptionChange}
            readOnly={!this.canEdit()}
          />
        </div>
      </div>
    );
  }

  render() {
    try {
    const restaurant:Restaurant = this.props.restaurant;
    return (
      <div className="edit_restaurant_form_view__public">
        <form
          className="form-horizontal"
          noValidate
          onSubmit={this.onSubmit}
        >
          {this.renderTimeZone()}
          {this.renderLocale()}
          {this.renderLogo()}
          {this.renderPicture()}
          {this.renderTag()}
          {this.renderWebsite()}
          {this.renderFacebook()}
          {this.renderTwitter()}
          {this.renderEmail()}
          {this.renderPhoneNumber()}
          {this.renderStreet()}
          {this.renderPostalCode()}
          {this.renderCity()}
          {this.renderCountry()}
          {this.renderCoordinates()}
          {this.renderDescription()}

          {this.renderButtons()}
        </form>

      </div>
    );

  } catch (e) { console.error(e); }
  }

}
